import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle2, AlertCircle, Wrench } from "lucide-react";
import Hero from "@/components/ui/Hero";
import TrustStrip from "@/components/ui/TrustStrip";
import CTABlock from "@/components/ui/CTABlock";
import FAQAccordion from "@/components/ui/FAQAccordion";
import CompletedWorkGrid from "@/components/ui/CompletedWorkGrid";
import Reveal from "@/components/ui/Reveal";
import ServiceIdentityStrip from "@/components/ui/ServiceIdentityStrip";
import AskBeforeYouForget from "@/components/forms/AskBeforeYouForget";
import { SERVICE_BY_SLUG, SERVICES } from "@/data/services";
import { SERVICES_THAT_SHOW_COMPLETED_WORK } from "@/data/galleryPhotos";
import { BUSINESS } from "@/lib/constants";

export default function ServicePage() {
    const { slug } = useParams();
    const service = SERVICE_BY_SLUG[slug];

    useEffect(() => {
        if (service) {
            document.title = service.meta_title;
            const meta = document.querySelector('meta[name="description"]') || (() => {
                const m = document.createElement("meta");
                m.setAttribute("name", "description");
                document.head.appendChild(m);
                return m;
            })();
            meta.setAttribute("content", service.meta_description);
        }
    }, [service]);

    if (!service) return <Navigate to="/services" replace />;

    const related = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 2);

    return (
        <>
            <Hero
                eyebrow="Pool Cage & Screen Repair"
                title={service.h1}
                subtitle={service.hero_summary}
                primaryCta={{ to: "/free-estimate", label: service.primary_cta }}
                tertiary={{ to: "/upload-photos", label: "Have photos? Upload them for technician review" }}
            />
            <TrustStrip />
            <ServiceIdentityStrip service={service} />

            {/* What We Do */}
            <section className="a1-section">
                <div className="a1-container max-w-4xl">
                    <p className="a1-eyebrow mb-3">What We Handle</p>
                    <h2 className="mb-6">What We {service.slug.includes("repair") ? "Repair" : "Do"}</h2>
                    <ul className="grid sm:grid-cols-2 gap-3">
                        {service.what_we_do.map((item, idx) => (
                            <li key={idx} className="a1-card flex items-start gap-3">
                                <CheckCircle2 size={22} className="text-[var(--color-accent)] shrink-0 mt-0.5" />
                                <span className="text-[var(--color-text)] leading-relaxed">{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Common Problems */}
            <section className="a1-section a1-bg-alt">
                <div className="a1-container max-w-4xl">
                    <p className="a1-eyebrow mb-3">Common Problems</p>
                    <h2 className="mb-6">Common Problems We See</h2>
                    <ul className="grid sm:grid-cols-2 gap-3">
                        {service.common_problems.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-3 rounded-xl bg-[var(--color-bg-card)] border border-[var(--color-line)] p-4">
                                <AlertCircle size={20} className="text-[var(--color-storm)] shrink-0 mt-0.5" />
                                <span className="text-[var(--color-text)] leading-relaxed">{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Process steps */}
            <section className="a1-section">
                <div className="a1-container max-w-4xl">
                    <p className="a1-eyebrow mb-3">How It Works</p>
                    <h2 className="mb-8">Simple, No-Pressure Process</h2>
                    <ol className="grid gap-5">
                        {service.process.map((step, idx) => (
                            <li key={idx} className="flex gap-5 items-start">
                                <div className="a1-step-num">{idx + 1}</div>
                                <p className="text-lg leading-relaxed pt-2 text-[var(--color-text)]">{step}</p>
                            </li>
                        ))}
                    </ol>
                    <div className="mt-10 flex flex-col sm:flex-row gap-3">
                        <Link to="/free-estimate" className="a1-btn-primary">{service.primary_cta} <ArrowRight size={18} /></Link>
                        <a href={`tel:${BUSINESS.phoneTel}`} className="a1-btn-secondary">
                            <Phone size={18} /> Call {BUSINESS.phone}
                        </a>
                    </div>
                </div>
            </section>

            {/* Repair size categories — Feb 2026 (John's verified guidance) */}
            {service.repair_size_categories ? (
                <section className="a1-section a1-bg-alt" data-testid="svc-repair-size-categories">
                    <div className="a1-container max-w-5xl">
                        <p className="a1-eyebrow mb-3">How Big Is Your Repair?</p>
                        <h2 className="mb-4 a1-h2-underline">{service.repair_size_categories.heading}</h2>
                        <p className="text-[var(--color-text-muted)] text-lg leading-relaxed mb-7">
                            {service.repair_size_categories.intro}
                        </p>
                        <div className="grid md:grid-cols-3 gap-5">
                            {service.repair_size_categories.categories.map((cat, i) => (
                                <div key={cat.label} className="a1-card a1-card-feature flex flex-col gap-3">
                                    <div className="a1-step-num">{i + 1}</div>
                                    <h3 className="text-lg leading-tight">{cat.label}</h3>
                                    <p className="text-sm font-bold uppercase tracking-wider" style={{ color: "var(--color-accent)" }}>
                                        {cat.panels}
                                    </p>
                                    <p className="text-[var(--color-text-muted)] leading-relaxed">{cat.description}</p>
                                </div>
                            ))}
                        </div>
                        <p className="a1-helper mt-7">
                            {service.repair_size_categories.cautious_footer}
                        </p>
                    </div>
                </section>
            ) : null}

            {/* Repair vs Rescreen comparison */}
            {service.repair_or_rescreen ? (
                <section className="a1-section">
                    <div className="a1-container max-w-5xl">
                        <p className="a1-eyebrow mb-3">Repair or Rescreen?</p>
                        <h2 className="mb-6 a1-h2-underline">Repair It or Rescreen the Whole Cage?</h2>
                        <div className="grid md:grid-cols-3 gap-5">
                            {Object.entries(service.repair_or_rescreen).map(([k, v]) => (
                                <div key={k} className="a1-card a1-card-feature flex flex-col gap-3">
                                    <div className="a1-icon-wrap">
                                        <Wrench size={22} />
                                    </div>
                                    <h3 className="text-lg capitalize">{k.replace(/_/g, " ")}</h3>
                                    <p className="text-[var(--color-text-muted)] leading-relaxed">{v}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            ) : null}

            {/* Pricing guidance (Screen Door page) — Feb 2026 */}
            {service.pricing_guidance ? (
                <section className="a1-section a1-bg-alt" data-testid="svc-pricing-guidance">
                    <div className="a1-container max-w-4xl">
                        <p className="a1-eyebrow mb-3">Pricing Guidance</p>
                        <h2 className="mb-4 a1-h2-underline">{service.pricing_guidance.heading}</h2>
                        <p className="text-[var(--color-text-muted)] text-lg leading-relaxed mb-7">
                            {service.pricing_guidance.intro}
                        </p>
                        <div className="grid sm:grid-cols-2 gap-5 mb-8">
                            {service.pricing_guidance.items.map((item) => (
                                <div key={item.label} className="a1-card a1-card-feature flex flex-col gap-2">
                                    <h3 className="text-lg leading-tight">{item.label}</h3>
                                    <p className="text-2xl font-bold serif" style={{ color: "var(--color-accent)" }}>
                                        {item.approx}
                                    </p>
                                    <p className="text-[var(--color-text-muted)] leading-relaxed">{item.includes}</p>
                                    <p className="a1-helper mt-1">{item.disclaimer}</p>
                                </div>
                            ))}
                        </div>
                        {service.pricing_guidance.custom_doors ? (
                            <div className="a1-card a1-card-feature flex flex-col gap-3" data-testid="svc-custom-doors">
                                <h3 className="text-lg leading-tight">{service.pricing_guidance.custom_doors.heading}</h3>
                                <p className="text-[var(--color-text-muted)] leading-relaxed">
                                    {service.pricing_guidance.custom_doors.body}
                                </p>
                                <a
                                    href={service.pricing_guidance.custom_doors.vendor_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="a1-btn-secondary self-start"
                                    data-testid="svc-pca-link"
                                >
                                    {service.pricing_guidance.custom_doors.vendor_label}
                                </a>
                                <p className="a1-helper">{service.pricing_guidance.custom_doors.disclaimer}</p>
                            </div>
                        ) : null}
                        <p className="a1-helper mt-6 text-center">{service.pricing_guidance.footer}</p>
                    </div>
                </section>
            ) : null}

            {/* Condo pricing caveats — Feb 2026 */}
            {service.pricing_caveats ? (
                <section className="a1-section a1-bg-alt" data-testid="svc-pricing-caveats">
                    <div className="a1-container max-w-4xl">
                        <p className="a1-eyebrow mb-3">Condo Pricing</p>
                        <h2 className="mb-4 a1-h2-underline">{service.pricing_caveats.heading}</h2>
                        <p className="text-[var(--color-text-muted)] text-lg leading-relaxed mb-5">
                            {service.pricing_caveats.body}
                        </p>
                        <div className="a1-card a1-card-feature">
                            <p className="text-[12px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: "var(--color-accent)" }}>
                                Pricing factors we review
                            </p>
                            <ul className="grid sm:grid-cols-2 gap-2">
                                {service.pricing_caveats.factors.map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-[var(--color-text)]">
                                        <CheckCircle2 size={18} className="text-[var(--color-accent)] shrink-0 mt-0.5" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <p className="a1-helper mt-6">{service.pricing_caveats.disclaimer}</p>
                    </div>
                </section>
            ) : null}

            {/* AEO direct-answer blocks (above FAQ) — short, conversational, cautious */}
            {service.aeo_answers && service.aeo_answers.length ? (
                <section className="a1-section" data-testid="svc-aeo-answers">
                    <div className="a1-container max-w-3xl">
                        <p className="a1-eyebrow mb-3">Quick Answers</p>
                        <h2 className="mb-6 a1-h2-underline">Direct Answers</h2>
                        <div className="space-y-5">
                            {service.aeo_answers.map((row, i) => (
                                <div key={i} className="a1-card a1-card-feature">
                                    <h3 className="text-base leading-snug mb-2">{row.q}</h3>
                                    <p className="text-[var(--color-text-muted)] leading-relaxed">{row.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            ) : null}

            {/* Real A1 Screening project photos — shown on pool-cage / pool-rescreening pages only */}
            {SERVICES_THAT_SHOW_COMPLETED_WORK.has(service.slug) ? (
                <section className="a1-section" data-testid="svc-completed-work">
                    <div className="a1-container max-w-5xl">
                        <Reveal>
                            <p className="a1-eyebrow mb-3">Project Photos</p>
                            <h2 className="mb-4 a1-h2-underline">Pool Cage & Screen Enclosure Project Photos</h2>
                            <p className="text-[var(--color-text-muted)] text-lg leading-relaxed mb-7 max-w-3xl">
                                Real pool cage and screen enclosure project photos from A1 Screening. Final recommendations and pricing are confirmed after technician review.
                            </p>
                        </Reveal>
                        <CompletedWorkGrid surface="service" limit={3} />
                        <Reveal delay={120}>
                            <p className="a1-helper mt-6">
                                <Link to="/gallery" className="!underline">See more project photos →</Link>
                            </p>
                        </Reveal>
                    </div>
                </section>
            ) : null}

            {/* FAQ */}
            <section className="a1-section a1-bg-alt">
                <div className="a1-container max-w-3xl">
                    <p className="a1-eyebrow mb-3">FAQ</p>
                    <h2 className="mb-6 a1-h2-underline">Frequently Asked Questions</h2>
                    <FAQAccordion items={service.faqs} prefix={`svc-${service.slug}-faq`} />
                </div>
            </section>

            {/* Micro-form */}
            <section className="a1-section">
                <div className="a1-container">
                    <AskBeforeYouForget />
                </div>
            </section>

            {/* Related */}
            <section className="a1-section">
                <div className="a1-container max-w-4xl">
                    <p className="a1-eyebrow mb-3">Related Services</p>
                    <h3 className="mb-6">Other Services Naples Homeowners Ask About</h3>
                    <div className="grid sm:grid-cols-2 gap-5">
                        {related.map((s) => (
                            <Link key={s.slug} to={`/services/${s.slug}`} className="a1-card a1-card-link no-underline flex flex-col gap-3">
                                <h4 className="text-lg font-semibold leading-tight">{s.name}</h4>
                                <p className="text-sm text-[var(--color-text-muted)] leading-relaxed flex-1">{s.hero_summary}</p>
                                <span className="a1-btn-ghost mt-1">Learn more <ArrowRight size={14} /></span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <CTABlock />
        </>
    );
}
